import React, { useEffect, useRef, useState } from "react";
import { VIDEO_BUFFER } from "../../constants/general";

interface EmbedVideoPlayerProps {
  src: string;
  title?: string;
  autoplay?: boolean;
  duration?: number;
  enableAutoComplete?: boolean;
  onEnded: () => void;
  onError?: (error: string) => void;
  className?: string;
  fillMode?: "contain" | "cover";
}

const EmbedVideoPlayer: React.FC<EmbedVideoPlayerProps> = ({
  src,
  title = "Embedded Stream",
  autoplay = true,
  duration = 120,
  enableAutoComplete = true,
  onEnded,
  onError,
  className = "embedded-video-player",
  fillMode = "cover",
}) => {
  const completionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!src) {
      onError?.("Missing embedded source URL");
      return;
    }

    setLoaded(false);

    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }

    if (enableAutoComplete) {
      completionTimerRef.current = setTimeout(
        () => {
          onEnded();
        },
        (duration + VIDEO_BUFFER) * 1000,
      );
    }

    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
  }, [src, duration, enableAutoComplete, onEnded, onError]);

  return (
    <div
      className={`${className} embedded-video-container`}
      style={{ width: "100%", height: "100%", position: "relative" }}
    >
      {!loaded && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.08)",
            color: "#444",
            fontSize: "14px",
            zIndex: 1,
          }}
        >
          Loading embedded page...
        </div>
      )}
      <iframe
        src={src}
        title={title}
        onLoad={() => setLoaded(true)}
        style={{
          width: "100%",
          height: "100%",
          border: 0,
          objectFit: fillMode,
          background: "transparent",
        }}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
        loading={autoplay ? "eager" : "lazy"}
      />
    </div>
  );
};

export default React.memo(EmbedVideoPlayer);
